import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  Building2,
  Home,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  MoreHorizontal,
  Calendar,
  FileText,
  MapPin,
  Users,
  DollarSign,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Layers,
  CheckCircle,
  Loader2,
  Zap,
  Download,
  Navigation
} from 'lucide-react';
import { apiService } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/DropdownMenu';
import { Input } from '../../components/ui/Input';
import { PropertyStatusPill, UnitStatusPill } from '../../components/ui/StatusPills';
import PageHeader from '../../components/ui/PageHeader';
import AddPropertyDrawer from '../../components/modals/AddPropertyDrawer';
import LeaseEditorModal from '../../components/modals/LeaseEditorModal';
import { PropertiesPageSkeleton, PropertyPortfolioSectionSkeleton } from '../../components/ui/PageRouteSkeleton';
import { RESUME_ADD_PROPERTY_EVENT } from '../../utils/settingsReturnFlow';
import PropertyReportPeriodModal from '../../components/modals/PropertyReportPeriodModal';
import { exportPropertyConsolidatedReport } from '../../utils/propertyConsolidatedReport';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/ui/Icon';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toaster';
import { ConfirmationDialog } from '../../components/ui/ConfirmationDialog';
import { Modal } from '../../components/ui/Modal';
import { usePermissions } from '../../hooks/usePermissions';
import { getPermissionErrorMessage, getPermissionTooltip } from '../../utils/permissionMessages';
import { utilityManagementService } from '../../services/utilityManagementService';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
`;

const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;


const StyledBuildingIcon = styled(Building2)`
  color: ${props => props.theme.primary};
`;

const StatsGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled(Card)`
  box-shadow: ${props => props.theme.shadowSoft};
  transition: ${props => props.theme.transitionSmooth};
  padding: 0.75rem !important;
  position: relative;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowElegant};
    transform: translateY(-2px);
  }
  
  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    text-align: center;
  }
`;

/** Aligned with payments info cards / StatsGrid: tinted rounded tile behind icon */
const StatIcon = styled.div<{ $color: string }>`
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) =>
    `color-mix(in srgb, ${props.$color} 32%, transparent)`};
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 12px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  @media (max-width: 640px) {
    position: static;
    margin: 0 auto 2px auto;
    order: 1;
  }
`;

const StatValue = styled.div<{ $color?: string }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.$color || props.theme.foreground};
  margin-bottom: 2px;
  
  @media (max-width: 640px) {
    order: 2;
    margin-bottom: 1px;
    font-size: 1.125rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 4px;
`;

const StatSubtext = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  
  @media (max-width: 640px) {
    order: 3;
    font-size: 0.625rem;
  }
`;

const PropProgressBar = styled.div<{ $pct: number; $color: string }>`
  height: 5px;
  border-radius: 4px;
  background: ${p => p.theme.muted};
  margin: 8px 0 4px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: ${p => p.$pct}%;
    height: 100%;
    border-radius: 4px;
    background: ${p => p.$color};
    transition: width 0.6s cubic-bezier(.4,0,.2,1);
  }
`;

const StatCardAccent = styled(StatCard) <{ $accent: string }>`
  overflow: hidden;
`;

const StatFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: ${p => p.theme.mutedForeground};
  border-top: 1px solid ${p => p.theme.border};
  padding-top: 10px;
  margin-top: 8px;
`;

// Gradient text utility for stat values and icons
const GradientText = styled.span`
  background: linear-gradient(90deg, hsl(104 98% 38%), hsl(88 100% 50%));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.mutedForeground};
  width: 16px;
  height: 16px;
  z-index: 1;
`;



const RefreshButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.foreground};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const PropertyGrid = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 2px 0;
  margin: 0 -16px;
  scroll-snap-type: x mandatory;
  scroll-padding: 0;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  /* Add padding to first and last child for proper spacing */
  & > *:first-child {
    margin-left: 16px;
  }
  
  & > *:last-child {
    margin-right: 16px;
  }
  
  @media (min-width: 640px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    overflow-x: visible;
    margin: 0;
    padding: 2px 0;
    scroll-padding: 0;
    
    & > *:first-child {
      margin-left: 0;
    }
    
    & > *:last-child {
      margin-right: 0;
    }
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PropertyCard = styled(Card)`
  box-shadow: ${props => props.theme.shadowSoft};
  transition: ${props => props.theme.transitionSmooth};
  background:
    linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%),
    ${props => props.theme.card};
  min-width: 280px;
  scroll-snap-align: start;
  margin-top: 2px;
  margin-bottom: 2px;
  overflow: hidden;
  
  @media (min-width: 640px) {
    min-width: auto;
    width: 100%;
  }
  
  &:hover {
    box-shadow: ${props => props.theme.shadowElegant};
    transform: translateY(-3px);
    margin-top: 4px;
    margin-bottom: 0;
    border-color: ${props => props.theme.primary}66;
  }
`;

const PropertyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
`;

const PropertyInfo = styled.div`
  flex: 1;
`;

const PropertyName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.foreground};
  margin-bottom: 7px;
  line-height: 1.25;
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const PropertyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.mutedForeground};
  font-size: 0.875rem;
  padding: 8px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background: ${props => props.theme.muted}20;
`;

const LocationInlineMeta = styled.span<{ $tone?: string }>`
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.$tone || p.theme.mutedForeground};
`;

const MapActionButton = styled.button`
  margin-left: auto;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  font-size: 12px;
  padding: 4px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${(p) => p.theme.muted};
  }
`;

const GoogleDotIcon = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  background: conic-gradient(#4285f4 0 25%, #34a853 25% 50%, #fbbc05 50% 75%, #ea4335 75% 100%);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 8px 0;
`;

const InfoBox = styled.div`
  background: ${props => props.theme.muted}18;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 12px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-weight: 600;
  color: ${props => props.theme.foreground};
`;

const RevenueBox = styled.div`
  background: linear-gradient(180deg, hsl(104 98% 38% / 0.1), hsl(104 98% 38% / 0.04));
  border: 1px solid hsl(104 98% 38% / 0.25);
  border-radius: 10px;
  padding: 12px;
  margin: 4px 0 2px;
`;

const RevenueValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.success};
  margin-bottom: 4px;
`;

const RevenueLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 2px;
`;

const RevenueSubtext = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;

const OccupancyText = styled.div`
  font-size: 12px;
  color: ${props => props.theme.success};
`;

const PerUnitText = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;




const FilterInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 0.875rem;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring};
    box-shadow: 0 0 0 2px ${props => props.theme.ring}20;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 0.875rem;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring};
    box-shadow: 0 0 0 2px ${props => props.theme.ring}20;
  }
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.border};
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  font-size: 0.875rem;
  cursor: pointer;
  transition: ${props => props.theme.transitionSmooth};
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const FilterButtonPrimary = styled(FilterButton)`
  background: ${props => props.theme.primary};
  color: ${props => props.theme.primaryForeground};
  border-color: ${props => props.theme.primary};
  
  &:hover {
    background: ${props => props.theme.primary}E6;
  }
`;

const FilterButtonContainer = styled.div`
  position: relative;
`;

const FilterIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: ${props => props.theme.primary};
  border-radius: 50%;
  border: 2px solid ${props => props.theme.background};
`;

const CardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${props => props.theme.border};
`;

const PropertyId = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
`;

const ActionButtonsSmall = styled.div`
  display: flex;
  gap: 6px;
  button:active {
    transform: scale(0.96);
    opacity: 0.9;
  }
  button[aria-busy="true"] {
    pointer-events: none;
    opacity: 0.85;
  }
`;

const UtilityQuickAction = styled(Button)`
  border-color: hsl(38 92% 44% / 0.45) !important;
  color: hsl(38 92% 36%) !important;
  background: hsl(38 92% 50% / 0.08) !important;

  &:hover {
    background: hsl(38 92% 50% / 0.14) !important;
  }
`;

const ViewPropertyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ViewPropertyItem = styled.div`
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.muted}15;
  border-radius: 10px;
  padding: 10px 12px;
`;

const ViewPropertyLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 4px;
`;

const ViewPropertyValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
`;

const TenantInfoBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${props => props.theme.muted}20;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
`;

const TenantInfoLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin-bottom: 12px;
`;

const TenantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TenantItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TenantName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
`;

const TenantDetails = styled.div`
  font-size: 11px;
  color: ${props => props.theme.mutedForeground};
`;

const TenantMore = styled.div`
  font-size: 11px;
  color: ${props => props.theme.mutedForeground};
  font-style: italic;
  text-align: center;
  padding: 4px 0;
`;

const PropertyMetadata = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  margin-top: 8px;
`;

const MetadataItem = styled.div`
  span {
    color: ${props => props.theme.mutedForeground};
  }
  
  div {
    font-weight: 500;
    color: ${props => props.theme.foreground};
  }
`;


const ErrorMessage = styled.div`
  background: ${props => props.theme.destructive}10;
  border: 1px solid ${props => props.theme.destructive}30;
  color: ${props => props.theme.destructive};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.destructive};
  cursor: pointer;
  font-size: 1.25rem;
  padding: 4px;
  
  &:hover {
    opacity: 0.7;
  }
`;

interface Property {
  id: string;
  name: string;
  location: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  geocodeConfidence?: number | null;
  coordinatesMissing?: boolean;
  type: string;
  totalUnits: number;
  status: string;
  monthlyRevenue?: number;
  createdAt: string;
  unitNamingConvention: string;
  customNamingPattern?: string;
  unitTypeConfigs?: Array<{
    id: string;
    unitType: string;
    rent: number;
    deposit: number;
  }>;
  units?: Array<{
    id: string;
    status: string;
    unitNumber: string;
    rent: number;
    unitType?: string;
    tenant?: {
      id: string;
      name: string;
      phone: string;
      email?: string;
    } | null;
  }>;
}

interface UtilitySetupDraft {
  name: string;
  utilityType: string;
  mode: 'SHARED' | 'INDIVIDUAL';
  appliesToUnitTypes?: string[];
  houseNumbers?: string;
  enabled?: boolean;
}

const Properties: React.FC = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission, user } = usePermissions();

  // Only run when auth is ready so the API request has a valid token (avoids intermittent 401 / empty list)
  const authReady = isAuthenticated && !authLoading;

  // React Query: short-term cache (staleTime 5m) — list refetches when invalidated after mutations
  const { data: properties = [], isLoading: isLoadingProperties, isFetching: isFetchingProperties, error: propertiesError, refetch: refetchProperties } = useQuery({
    queryKey: ['properties'],
    queryFn: async () => {
      const response = await apiService.getProperties();

      // Handle response structure
      let propertiesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          propertiesData = response.data;
        } else if (response.data.properties && Array.isArray(response.data.properties)) {
          propertiesData = response.data.properties;
        }
      }

      return propertiesData;
    },
    enabled: authReady,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: unknown) => {
      const isNetwork = error && typeof error === 'object' && (error as { code?: string }).code === 'ERR_NETWORK';
      if (isNetwork) return false; // Don't retry on CORS/network errors
      const status = error && typeof error === 'object' && (error as { response?: { status?: number } }).response?.status;
      if (status === 503 || status === 504) return false; // Don't retry on service unavailable / gateway timeout
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  // Handle errors from React Query (single user-friendly toast, no verbose console)
  const propertiesErrorShown = React.useRef(false);
  useEffect(() => {
    if (!propertiesError) {
      propertiesErrorShown.current = false;
      return;
    }
    if (propertiesErrorShown.current) return;
    propertiesErrorShown.current = true;
    const message = propertiesError instanceof Error ? propertiesError.message : 'Connection problem';
    toast.error('Unable to load properties', {
      description: message === 'Network Error' ? 'Check your connection or try again later.' : message,
    });
  }, [propertiesError, toast]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);

  useEffect(() => {
    const resumeAddProperty = () => setShowAddModal(true);
    window.addEventListener(RESUME_ADD_PROPERTY_EVENT, resumeAddProperty);
    return () => window.removeEventListener(RESUME_ADD_PROPERTY_EVENT, resumeAddProperty);
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<string | null>(null);
  const [isDeletingProperty, setIsDeletingProperty] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [viewingProperty, setViewingProperty] = useState<Property | null>(null);
  const [leaseEditorProperty, setLeaseEditorProperty] = useState<Property | null>(null);
  const [leaseEditorLeaseId, setLeaseEditorLeaseId] = useState<string | null>(null);
  const [isRegeocodingPropertyId, setIsRegeocodingPropertyId] = useState<string | null>(null);
  const [reportProperty, setReportProperty] = useState<Property | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [planModalData, setPlanModalData] = useState<{
    amount: number | null;
    isCustom: boolean;
    bandLabel?: string;
    currency?: string;
  } | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    minUnits: '',
    maxUnits: '',
    minRevenue: '',
    maxRevenue: ''
  });

  const isRefreshingPortfolio = isFetchingProperties && !isLoadingProperties;

  const handleRefreshPortfolio = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['properties'] });
    await refetchProperties();
  }, [queryClient, refetchProperties]);

  // Tabs for filtering properties
  const tabs = [
    { value: 'all', label: 'All Properties', count: properties.length },
    { value: 'active', label: 'Active', count: properties.filter((p: Property) => p.status === 'active').length },
    { value: 'inactive', label: 'Inactive', count: properties.filter((p: Property) => p.status === 'inactive').length },
    { value: 'maintenance', label: 'Maintenance', count: properties.filter((p: Property) => p.status === 'maintenance').length }
  ];

  // Action buttons
  const canCreateProperty = hasPermission('canCreate') && hasPermission('canManageProperties');

  const actionButtons = [
    {
      label: 'Add Property',
      icon: <Plus size={16} />,
      variant: 'primary' as const,
      size: 'md' as const,
      onClick: () => {
        if (canCreateProperty) {
          setShowAddModal(true);
        } else {
          const errorMessage = getPermissionErrorMessage({
            requiredPermission: !hasPermission('canCreate') ? 'canCreate' : 'canManageProperties',
            userRole: user?.role,
            action: 'create properties'
          });
          toast.error('Permission Denied', {
            description: errorMessage,
            duration: 8000,
          });
        }
      },
      disabled: !canCreateProperty,
      title: !canCreateProperty ? getPermissionTooltip(!hasPermission('canCreate') ? 'canCreate' : 'canManageProperties', 'create properties') : undefined
    }
  ];


  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-dropdown]')) {
        // setOpenDropdown(null); // This line is removed
      }
      if (!target.closest('[data-filter-dropdown]')) {
        // setShowFilterDropdown(false); // This line is removed
      }

    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavigateToUnits = (propertyId: string, status?: string) => {
    if (status === 'CLOSED') {
      return; // Blocked — suspended properties are not accessible
    }
    navigate(`/properties/${propertyId}/units`);
  };

  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
    // Update status filter based on tab
    if (tabValue === 'all') {
      setStatusFilter('');
    } else {
      setStatusFilter(tabValue);
    }
  };

  // Mutation for creating/updating properties (invalidates cache)
  const propertyMutation = useMutation({
    mutationFn: async ({ property, isEdit }: { property: any; isEdit: boolean }) => {
      if (isEdit) {
        return apiService.updateProperty(property.id, property);
      } else {
        return apiService.createProperty(property);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch properties
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast.success('Property saved successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to save property', {
        description: error.message || 'An error occurred',
      });
    },
  });

  const handleEditProperty = async (property: Property) => {
    const loadingToast = toast.loading('Loading property...');
    try {
      const response = await apiService.getProperty(property.id);
      const fullProperty = response.data?.property || response.data || property;
      console.log('📋 [Properties] Loaded property for editing:', fullProperty);
      setEditingProperty(fullProperty);
    } catch (err: any) {
      console.error('❌ [Properties] Error loading property:', err);
      const errorMessage = err.message || 'Failed to load property details';
      setError(errorMessage);
      toast.error('Failed to load property', {
        description: errorMessage,
      });
      setEditingProperty(property);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const configureUtilitiesFromSetup = async (propertyId: string, setupRules: UtilitySetupDraft[]) => {
    const activeRules = (setupRules || []).filter(rule => rule.enabled !== false && rule.name?.trim());
    if (!activeRules.length) return { created: 0, failed: 0 };

    const propertyRes = await apiService.getProperty(propertyId);
    const propertyData = propertyRes.data?.property || propertyRes.data;
    const units: Array<{ id: string; unitNumber: string; unitType?: string }> = propertyData?.units || [];

    const parseHouseNumbers = (value?: string) =>
      (value || '')
        .split(/[\n,]/g)
        .map(v => v.trim().toUpperCase())
        .filter(Boolean);

    let created = 0;
    let failed = 0;

    for (const rule of activeRules) {
      const houseNumbers = parseHouseNumbers(rule.houseNumbers);
      const typedUnits = (rule.appliesToUnitTypes?.length
        ? units.filter(u => !!u.unitType && rule.appliesToUnitTypes!.includes(String(u.unitType)))
        : units
      );

      const matchedUnits = houseNumbers.length
        ? typedUnits.filter(u => houseNumbers.includes(String(u.unitNumber || '').toUpperCase()))
        : typedUnits;

      if (!matchedUnits.length) {
        failed += 1;
        continue;
      }

      if (rule.mode === 'INDIVIDUAL') {
        for (const unit of matchedUnits) {
          try {
            await utilityManagementService.createGroup({
              propertyId,
              name: `${rule.name.trim()} • ${unit.unitNumber}`,
              utilityType: rule.utilityType,
              unitIds: [unit.id],
            });
            created += 1;
          } catch {
            failed += 1;
          }
        }
      } else {
        try {
          await utilityManagementService.createGroup({
            propertyId,
            name: rule.name.trim(),
            utilityType: rule.utilityType,
            unitIds: matchedUnits.map(u => u.id),
          });
          created += 1;
        } catch {
          failed += 1;
        }
      }
    }

    return { created, failed };
  };


  const handleCreateProperty = async (formData: any) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Check permissions before attempting to create property
      if (!hasPermission('canCreate') || !hasPermission('canManageProperties')) {
        const errorMessage = getPermissionErrorMessage({
          requiredPermission: !hasPermission('canCreate') ? 'canCreate' : 'canManageProperties',
          userRole: user?.role,
          action: 'create properties'
        });

        setError(errorMessage);
        toast.error('Permission Denied', {
          description: errorMessage,
          duration: 8000,
        });
        setIsSubmitting(false);
        return;
      }

      const prevList = queryClient.getQueryData<Property[]>(['properties']);
      const prevCount = Array.isArray(prevList) ? prevList.length : 0;

      // No global loader - form has its own loading state
      const createRes = await apiService.createProperty(formData);
      const createdPropertyId = createRes?.data?.property?.id || createRes?.data?.id;

      if (createdPropertyId && Array.isArray(formData.utilitySetup) && formData.utilitySetup.length > 0) {
        const utilityResult = await configureUtilitiesFromSetup(createdPropertyId, formData.utilitySetup);
        if (utilityResult.created > 0) {
          toast.success('Utilities pre-configured', {
            description: `${utilityResult.created} utility group(s) created from setup.`,
          });
        }
        if (utilityResult.failed > 0) {
          toast.info('Some utility rules were skipped', {
            description: `${utilityResult.failed} setup rule(s) did not match units or failed to save.`,
          });
        }
      }

      toast.success('Property created', {
        description: 'The property has been successfully created.',
      });

      setShowAddModal(false);

      // Invalidate cache to refetch properties
      queryClient.invalidateQueries({ queryKey: ['properties'] });

      // First property: show plan modal with estimate
      if (prevCount === 0) {
        try {
          const estRes = await apiService.get(`/pricing/estimate?units=${formData.totalUnits ?? 0}`);
          setPlanModalData(estRes.data);
          setShowPlanModal(true);
        } catch (_) {
          setPlanModalData({ amount: null, isCustom: true, bandLabel: 'Enterprise (100+)' });
          setShowPlanModal(true);
        }
      }
    } catch (err: any) {
      let errorMessage = err.message || 'Failed to create property';
      const errorData = err.response?.data;

      // Billing gate: 403 with redirectTo → open Settings → Billing
      if (err.response?.status === 403 && errorData?.redirectTo) {
        setSearchParams({ openSettings: 'billing' });
        const billingMessage = errorData.reason === 'past_due' || errorData.reason === 'suspended'
          ? 'Your payment is past due. Please pay in Settings → Billing or visit /billing.'
          : 'Please activate your subscription in Settings → Billing to add properties.';
        toast.error('Billing required', { description: billingMessage, duration: 8000 });
        setError(billingMessage);
        setIsSubmitting(false);
        return;
      }

      // Other 403 (permission denied)
      if (err.response?.status === 403) {
        if (err.permissionError) {
          errorMessage = err.permissionError.friendlyMessage;
        } else if (errorData?.code === 'INSUFFICIENT_PERMISSIONS') {
          errorMessage = getPermissionErrorMessage({
            requiredPermission: errorData.requiredPermission || 'canCreate',
            userRole: errorData.userRole || user?.role,
            action: 'create properties'
          });
        } else if (errorData?.code === 'AUTHZ_ERROR') {
          errorMessage = errorData.error || 'You do not have permission to create properties.';
        } else {
          errorMessage = 'You do not have permission to create properties. Please contact your administrator.';
        }
      }

      setError(errorMessage);
      console.error('Error creating property:', err);
      toast.error('Failed to create property', {
        description: errorMessage,
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProperty = async (formData: any) => {
    if (!editingProperty) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // No global loader - form has its own loading state
      console.log('🔄 [Properties] Updating property:', editingProperty.id, formData);

      const response = await apiService.updateProperty(editingProperty.id, formData);

      // Property updated successfully
      toast.success('Property updated', {
        description: 'The property has been successfully updated.',
      });

      setEditingProperty(null);

      // Invalidate cache to refetch properties
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || err.message || 'Failed to update property';

      // Billing gate: 403 with redirectTo → open Settings → Billing
      if (err.response?.status === 403 && errorData?.redirectTo) {
        setSearchParams({ openSettings: 'billing' });
        const billingMessage = errorData.reason === 'past_due' || errorData.reason === 'suspended'
          ? 'Your payment is past due. Please pay in Settings → Billing or visit /billing.'
          : 'Please activate your subscription in Settings → Billing to update properties.';
        toast.error('Billing required', { description: billingMessage, duration: 8000 });
        setError(billingMessage);
        setIsSubmitting(false);
        return;
      }

      setError(errorMessage);
      console.error('❌ [Properties] Error updating property:', err);
      toast.error('Failed to update property', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationPatched = (updatedProperty: any) => {
    if (updatedProperty?.id) {
      setEditingProperty(updatedProperty);
    }
    queryClient.invalidateQueries({ queryKey: ['properties'] });
  };

  const handleDeleteProperty = async (id: string) => {
    setPropertyToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteProperty = async () => {
    if (!propertyToDelete) return;

    setError(null);
    setIsDeletingProperty(true);
    try {
      await apiService.deleteProperty(propertyToDelete);

      toast.success('Property deleted', {
        description: 'The property has been successfully removed.',
      });

      queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to delete property';
      setError(errorMsg);
      console.error('Error deleting property:', err);
      toast.error('Failed to delete property', {
        description: errorMsg,
      });
    } finally {
      setIsDeletingProperty(false);
      setShowDeleteConfirm(false);
      setPropertyToDelete(null);
    }
  };



  // ─── Property consolidated report (period picker → multi-page PDF) ───
  const handleDownloadPropertyReport = async (month: number, year: number) => {
    if (!reportProperty) return;

    setIsDownloadingReport(true);
    const loadingToast = toast.loading('Building property report…');
    try {
      const result = await exportPropertyConsolidatedReport(
        reportProperty.id,
        month,
        year,
        (id, m, y) => apiService.downloadPropertyConsolidatedReportPdf(id, m, y),
        reportProperty.name,
      );

      toast.dismiss(loadingToast);
      if (result.ok) {
        setReportProperty(null);
        toast.success('Report downloaded', {
          description: result.filename,
        });
        return;
      }

      toast.error('Export failed', {
        description: result.message || 'Could not download the property report.',
      });
    } finally {
      setIsDownloadingReport(false);
    }
  };

  // Calculate stats
  const totalProperties = Array.isArray(properties) ? properties.length : 0;
  const totalUnits = Array.isArray(properties) ? properties.reduce((sum, prop) => sum + (prop.units?.length || 0), 0) : 0;
  const occupiedUnits = Array.isArray(properties) ? properties.reduce((sum: number, prop: Property) =>
    sum + (prop.units?.filter((unit: any) => unit.status === 'OCCUPIED').length || 0), 0
  ) : 0;
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  // Calculate total revenue from actual occupied units' rent
  const totalRevenue = Array.isArray(properties) ? properties.reduce((sum: number, prop: Property) => {
    const occupiedUnitsInProp = prop.units?.filter((unit: any) => unit.status === 'OCCUPIED') || [];
    const revenueFromProp = occupiedUnitsInProp.reduce((unitSum: number, unit: any) => unitSum + (unit.rent || 0), 0);
    return sum + revenueFromProp;
  }, 0) : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'success';
      case 'FULL':
        return 'warning';
      case 'MAINTENANCE':
        return 'warning';
      case 'CLOSED':
        return 'muted';
      default:
        return 'muted';
    }
  };

  const hasCoordinates = (property: Property) =>
    typeof property.latitude === 'number' && typeof property.longitude === 'number';

  const getLocationQuality = (property: Property): { label: 'Verified' | 'Approximate' | 'Missing'; tone: string } => {
    if (property.coordinatesMissing || !hasCoordinates(property)) {
      return { label: 'Missing', tone: 'var(--destructive)' };
    }
    if ((property.geocodeConfidence ?? 0) >= 0.65) {
      return { label: 'Verified', tone: 'var(--success)' };
    }
    return { label: 'Approximate', tone: 'hsl(38 92% 44%)' };
  };

  const getMapLinks = (property: Property) => {
    const q = encodeURIComponent(property.address || property.location || '');
    if (hasCoordinates(property)) {
      const lat = property.latitude as number;
      const lng = property.longitude as number;
      return {
        google: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
        apple: `https://maps.apple.com/?ll=${lat},${lng}&q=${q}`,
      };
    }
    return {
      google: `https://www.google.com/maps/search/?api=1&query=${q}`,
      waze: `https://waze.com/ul?q=${q}`,
      apple: `https://maps.apple.com/?q=${q}`,
    };
  };

  // Enhanced search and filter functionality
  const filteredProperties = Array.isArray(properties) ? properties.filter(property => {
    const searchMatch =
      property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.id.toLowerCase().includes(searchTerm.toLowerCase());

    if (!searchMatch) return false;

    // Apply tab filter (status filter)
    if (statusFilter && property.status !== statusFilter) return false;

    // Apply other filters
    if (filters.type && property.type !== filters.type) return false;

    const totalUnits = property.units?.length || 0;
    if (filters.minUnits && totalUnits < parseInt(filters.minUnits)) return false;
    if (filters.maxUnits && totalUnits > parseInt(filters.maxUnits)) return false;

    const monthlyRevenue = property.monthlyRevenue || 0;
    if (filters.minRevenue && monthlyRevenue < parseInt(filters.minRevenue)) return false;
    if (filters.maxRevenue && monthlyRevenue > parseInt(filters.maxRevenue)) return false;

    return true;
  }) : [];

  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      minUnits: '',
      maxUnits: '',
      minRevenue: '',
      maxRevenue: ''
    });
    setStatusFilter('');
    setActiveTab('all');
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handleReGeocodeProperty = async (property: Property) => {
    try {
      setIsRegeocodingPropertyId(property.id);
      const response = await apiService.reGeocodeProperty(property.id);
      const updated = response.data?.property || response.data;
      if (updated?.coordinatesMissing) {
        toast.warning('Location still unresolved', {
          description: response.data?.warning || 'Coordinates are still missing. Please verify the address and pin manually.',
        });
      } else {
        toast.success('Location re-geocoded', {
          description: 'Property coordinates were updated successfully.',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      if (viewingProperty?.id === property.id) {
        const latest = await apiService.getProperty(property.id);
        const refreshed = latest.data?.property || latest.data;
        setViewingProperty(refreshed);
      }
    } catch (err: any) {
      toast.error('Re-geocode failed', {
        description: err?.response?.data?.error || err?.message || 'Unable to re-geocode property location.',
      });
    } finally {
      setIsRegeocodingPropertyId(null);
    }
  };

  if (authLoading || (authReady && isLoadingProperties)) {
    return (
      <PageContainer>
        <ContentContainer>
          <PropertiesPageSkeleton />
        </ContentContainer>
      </PageContainer>
    );
  }

  // When properties fail to load (503, 504, network/CORS), show error state instead of empty dashboard
  if (authReady && !isLoadingProperties && propertiesError) {
    const errMsg = propertiesError instanceof Error ? propertiesError.message : 'Unable to load properties';
    const isServiceUnavailable = errMsg.includes('503') || errMsg.includes('504') || errMsg.includes('Network Error');
    return (
      <PageContainer>
        <ContentContainer>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--foreground)', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Icon name="Building2" size={32} />
              Property Management
            </h1>
            <p style={{ color: 'var(--muted-foreground)', fontSize: '1rem', lineHeight: '1.5', margin: '0' }}>
              Manage your properties, units, and tenant assignments
            </p>
          </div>
          <Card style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ color: 'var(--destructive)', marginBottom: '8px' }}>
              <strong>Unable to load properties</strong>
            </div>
            <p style={{ color: 'var(--muted-foreground)', marginBottom: '1rem' }}>
              {isServiceUnavailable ? 'The service is temporarily unavailable or took too long to respond. Please try again in a moment.' : errMsg}
            </p>
            <Button onClick={() => refetchProperties()} variant="primary">
              <RefreshCw size={16} style={{ marginRight: '8px' }} />
              Try again
            </Button>
          </Card>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        {/* Page Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--foreground)',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon name="Building2" size={32} />
            Property Management
          </h1>
          <p style={{
            color: 'var(--muted-foreground)',
            fontSize: '1rem',
            lineHeight: '1.5',
            margin: '0'
          }}>
            Manage your properties, units, and tenant assignments
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <ErrorMessage>
            <span><strong>Error:</strong> {error}</span>
            <ErrorCloseButton onClick={() => setError(null)}>×</ErrorCloseButton>
          </ErrorMessage>
        )}

        {/* Stats Cards */}
        <StatsGridStyled>

          {/* Total Properties */}
          <StatCardAccent $accent="hsl(215 80% 55%)">
            <CardContent>
              <StatIcon $color="hsl(215 80% 55%)">
                <Building2 />
              </StatIcon>
              <StatValue>{properties.length}</StatValue>
              <StatLabel>Total Properties</StatLabel>
              <StatSubtext>{totalUnits} units total</StatSubtext>
              <StatFooter>
                <Home size={11} />
                Across all locations
              </StatFooter>
            </CardContent>
          </StatCardAccent>

          {/* Total Units */}
          <StatCardAccent $accent="hsl(260 80% 60%)">
            <CardContent>
              <StatIcon $color="hsl(260 80% 60%)">
                <Layers />
              </StatIcon>
              <StatValue>{totalUnits}</StatValue>
              <StatLabel>Total Units</StatLabel>
              <StatSubtext>{occupiedUnits} occupied · {totalUnits - occupiedUnits} vacant</StatSubtext>
              <StatFooter>
                <CheckCircle size={11} color="hsl(142 72% 40%)" />
                {totalUnits - occupiedUnits} available for tenants
              </StatFooter>
            </CardContent>
          </StatCardAccent>

          {/* Occupancy Rate */}
          <StatCardAccent $accent="hsl(142 72% 40%)">
            <CardContent>
              <StatIcon $color="hsl(142 72% 40%)">
                <Users />
              </StatIcon>
              <StatValue $color={occupancyRate >= 80 ? 'hsl(142 72% 35%)' : occupancyRate >= 50 ? 'hsl(38 92% 40%)' : 'hsl(0 84% 50%)'}>
                {occupancyRate}%
              </StatValue>
              <StatLabel>Occupancy Rate</StatLabel>
              <PropProgressBar
                $pct={occupancyRate}
                $color={occupancyRate >= 80 ? 'hsl(142 72% 40%)' : occupancyRate >= 50 ? 'hsl(38 92% 48%)' : 'hsl(0 84% 55%)'}
              />
              <StatSubtext>{occupiedUnits} of {totalUnits} units occupied</StatSubtext>
            </CardContent>
          </StatCardAccent>

          {/* Monthly Revenue */}
          <StatCardAccent $accent="hsl(104 98% 38%)">
            <CardContent>
              <StatIcon $color="hsl(104 98% 38%)">
                <DollarSign />
              </StatIcon>
              <StatValue $color="hsl(104 98% 32%)" style={{ fontSize: totalRevenue >= 1_000_000 ? '1rem' : undefined }}>
                {totalRevenue >= 1_000_000
                  ? `KES ${(totalRevenue / 1_000_000).toFixed(1)}M`
                  : totalRevenue >= 1_000
                    ? `KES ${(totalRevenue / 1_000).toFixed(1)}K`
                    : `KES ${totalRevenue.toLocaleString()}`}
              </StatValue>
              <StatLabel>Monthly Revenue</StatLabel>
              <StatSubtext>From {occupiedUnits} active tenants</StatSubtext>
              <StatFooter>
                <ArrowUpRight size={11} color="hsl(142 72% 40%)" />
                Rent collected this month
              </StatFooter>
            </CardContent>
          </StatCardAccent>

        </StatsGridStyled>

        {/* Property Portfolio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="Building2" size={20} />
                Property Portfolio
              </div>
              <button
                onClick={() => void handleRefreshPortfolio()}
                disabled={isRefreshingPortfolio}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:outline-none disabled:opacity-50"
                title="Refresh properties"
                style={{
                  color: 'var(--success)',
                  outline: 'none',
                  border: 'none'
                }}
              >
                <RefreshCw size={18} style={isRefreshingPortfolio ? { animation: 'spin 1s linear infinite' } : undefined} />
              </button>
            </CardTitle>
          </CardHeader>

          {isRefreshingPortfolio ? (
            <PropertyPortfolioSectionSkeleton />
          ) : (
          <>
          <div style={{ paddingTop: '16px' }}>
            <PageHeader
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              searchPlaceholder="Search properties..."
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              actionButtons={actionButtons}
              showSearch={true}
              showTabs={true}
            />
          </div>

          <CardContent style={{ paddingTop: 'calc(1rem + 2px)', paddingBottom: 'calc(1rem + 2px)' }}>
            <PropertyGrid>
              {filteredProperties.map((property) => {
                const occupiedCount = property.units?.filter((unit: any) => unit.status === 'OCCUPIED').length || 0;
                const totalCount = property.units?.length || 0;
                const occupancyPercentage = totalCount > 0 ? Math.round((occupiedCount / totalCount) * 100) : 0;

                // Calculate average rent from unitTypeConfigs or fallback to units
                let averageRent = 0;
                if (property.unitTypeConfigs && property.unitTypeConfigs.length > 0) {
                  // Calculate weighted average rent based on unit type configs
                  const totalWeightedRent = property.unitTypeConfigs.reduce((sum: number, config: any) => {
                    // Find count of units of this type (approximate)        
                    const unitCount = property.units?.filter((u: any) => u.unitType === config.unitType).length || 1;
                    return sum + (config.rent * unitCount);
                  }, 0);
                  const totalUnitsWithConfigs = property.units?.length || property.totalUnits || 1;
                  averageRent = totalUnitsWithConfigs > 0 ? totalWeightedRent / totalUnitsWithConfigs :
                    (property.unitTypeConfigs[0]?.rent || 0);
                } else if (property.units && property.units.length > 0) {
                  // Fallback: calculate from actual units
                  const totalRent = property.units.reduce((sum: number, unit: any) => sum + (unit.rent || 0), 0);
                  averageRent = totalRent / property.units.length;
                }

                const locationQuality = getLocationQuality(property);
                const mapLinks = getMapLinks(property);
                return (
                  <PropertyCard key={property.id} style={property.status === 'CLOSED' ? { opacity: 0.75, position: 'relative' } : {}}>
                    {property.status === 'CLOSED' && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0,
                        background: '#fef2f2',
                        borderBottom: '2px solid #fca5a5',
                        padding: '7px 14px',
                        display: 'flex', alignItems: 'center', gap: 7,
                        fontSize: 13, fontWeight: 600, color: '#dc2626',
                        zIndex: 2, borderRadius: '12px 12px 0 0'
                      }}>
                        <span style={{ fontSize: 15 }}></span> Suspended by Administrator
                      </div>
                    )}
                    <CardContent style={property.status === 'CLOSED' ? { paddingTop: 44 } : {}}>
                      <PropertyHeader>
                        <PropertyInfo>
                          <PropertyName>{property.name}</PropertyName>
                          <BadgeContainer>
                            <PropertyStatusPill status={property.status as any} size="sm" />
                            <span style={{
                              padding: '4px 8px',
                              background: 'var(--muted)',
                              borderRadius: '6px',
                              fontSize: '12px',
                              color: 'var(--muted-foreground)'
                            }}>
                              {property.type}
                            </span>
                          </BadgeContainer>
                        </PropertyInfo>

                        <DropdownMenu align="end">
                          <DropdownMenuTrigger asChild>
                            <button style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '8px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--foreground)'
                            }}>
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                if (hasPermission('canUpdate') && hasPermission('canManageProperties')) {
                                  handleEditProperty(property);
                                } else {
                                  const errorMessage = getPermissionErrorMessage({
                                    requiredPermission: !hasPermission('canUpdate') ? 'canUpdate' : 'canManageProperties',
                                    userRole: user?.role,
                                    action: 'edit properties'
                                  });
                                  toast.error('Permission Denied', {
                                    description: errorMessage,
                                    duration: 8000,
                                  });
                                }
                              }}
                              disabled={!hasPermission('canUpdate') || !hasPermission('canManageProperties')}
                            >
                              <Edit size={16} />
                              Edit Property
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                toast.info('Maintenance management is coming soon!', {
                                  description: 'We\'re working on bringing you comprehensive maintenance management features. Stay tuned!'
                                });
                              }}
                            >
                              <Calendar size={16} />
                              Schedule Maintenance
                            </DropdownMenuItem>
                            {/* <DropdownMenuItem
                              onClick={() => {
                                if (hasPermission('canUpdate') && hasPermission('canManageProperties')) {
                                  setUtilityProperty(property);
                                } else {
                                  const errorMessage = getPermissionErrorMessage({
                                    requiredPermission: !hasPermission('canUpdate') ? 'canUpdate' : 'canManageProperties',
                                    userRole: user?.role,
                                    action: 'manage utilities'
                                  });
                                  toast.error('Permission Denied', {
                                    description: errorMessage,
                                    duration: 8000,
                                  });
                                }
                              }}
                              disabled={!hasPermission('canUpdate') || !hasPermission('canManageProperties')}
                            >
                              <Zap size={16} />
                              Manage Utilities
                            </DropdownMenuItem> */}
                            <DropdownMenuItem
                              className="destructive"
                              onClick={() => {
                                if (hasPermission('canDelete') && hasPermission('canManageProperties')) {
                                  handleDeleteProperty(property.id);
                                } else {
                                  const errorMessage = getPermissionErrorMessage({
                                    requiredPermission: !hasPermission('canDelete') ? 'canDelete' : 'canManageProperties',
                                    userRole: user?.role,
                                    action: 'delete properties'
                                  });
                                  toast.error('Permission Denied', {
                                    description: errorMessage,
                                    duration: 8000,
                                  });
                                }
                              }}
                              disabled={!hasPermission('canDelete') || !hasPermission('canManageProperties')}
                            >
                              <Trash2 size={16} />
                              Delete Property
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </PropertyHeader>

                      <PropertyDetails>
                        <DetailRow>
                          <MapPin size={16} />
                          <span>
                            {property.location}
                            <LocationInlineMeta $tone={locationQuality.tone}>
                              ({locationQuality.label === 'Approximate' ? 'Approx' : locationQuality.label})
                            </LocationInlineMeta>
                          </span>
                          <MapActionButton
                            onClick={() => window.open(mapLinks.google, '_blank', 'noopener,noreferrer')}
                            title="Open in Google Maps"
                          >
                            Open in <GoogleDotIcon />
                          </MapActionButton>
                        </DetailRow>

                        <InfoGrid>
                          <InfoBox>
                            <InfoLabel>Units</InfoLabel>
                            <InfoValue>{occupiedCount}/{totalCount}</InfoValue>
                            <OccupancyText>{occupancyPercentage}% occupied</OccupancyText>
                          </InfoBox>
                          <InfoBox>
                            <InfoLabel>Monthly Rent</InfoLabel>
                            <InfoValue>KES {Math.round(averageRent).toLocaleString()}</InfoValue>
                            <PerUnitText>avg per unit</PerUnitText>
                          </InfoBox>
                        </InfoGrid>

                        <RevenueBox>
                          <RevenueLabel>Monthly Revenue</RevenueLabel>
                          <RevenueValue>
                            KES {(Math.round(averageRent * occupiedCount)).toLocaleString()}
                          </RevenueValue>
                          <RevenueSubtext>{occupiedCount} active tenants</RevenueSubtext>
                        </RevenueBox>

                        <PropertyMetadata>
                          <MetadataItem>
                            <span>Added:</span>
                            <div>{new Date(property.createdAt).toLocaleDateString()}</div>
                          </MetadataItem>
                          <MetadataItem>
                            <span>Type:</span>
                            <div>{property.type}</div>
                          </MetadataItem>
                        </PropertyMetadata>
                      </PropertyDetails>

                      <CardActions>
                        {/* <PropertyId>ID: {property.id}</PropertyId> */}
                        <ActionButtonsSmall>
                          {/* <Button
                            variant="outline"
                            size="sm"
                            title="View property"
                            onClick={() => setViewingProperty(property)}
                          >
                            <Eye size={12} />
                          </Button> */}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={property.status === 'CLOSED'}
                            title={property.status === 'CLOSED' ? 'Property is suspended' : 'View property units'}
                            onClick={() => handleNavigateToUnits(property.id, property.status)}
                          >
                            <Eye size={12} />
                          </Button>
                          {/* <UtilityQuickAction
                            variant="outline"
                            size="sm"
                            title="Manage utilities"
                            onClick={() => {
                              if (hasPermission('canUpdate') && hasPermission('canManageProperties')) {
                                setUtilityProperty(property);
                              } else {
                                const errorMessage = getPermissionErrorMessage({
                                  requiredPermission: !hasPermission('canUpdate') ? 'canUpdate' : 'canManageProperties',
                                  userRole: user?.role,
                                  action: 'manage utilities'
                                });
                                toast.error('Permission Denied', {
                                  description: errorMessage,
                                  duration: 8000,
                                });
                              }
                            }}
                          >
                            <Zap size={12} />
                          </UtilityQuickAction> */}
                          <Button
                            variant="outline"
                            size="sm"
                            aria-busy={leaseEditorProperty?.id === property.id && !leaseEditorLeaseId}
                            aria-label="Open lease agreement"
                            onClick={async () => {
                              setLeaseEditorProperty(property);
                              setLeaseEditorLeaseId(null);
                              try {
                                const leases = await apiService.getPropertyLeases(property.id);
                                if (leases?.length) {
                                  setLeaseEditorLeaseId(leases[0].id);
                                }
                                // When no tenants yet: open in property-template mode so landlord can set lease once; new tenants will get it.
                              } catch {
                                toast.error('Could not load leases', { description: 'Try again later.' });
                                setLeaseEditorProperty(null);
                                setLeaseEditorLeaseId(null);
                              }
                            }}
                            title="Lease agreement"
                          >
                            {leaseEditorProperty?.id === property.id && !leaseEditorLeaseId ? (
                              <Loader2 size={12} className="spinner" />
                            ) : (
                              <FileText size={12} />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            title="Download full property report (summary, collections, occupancy, arrears)"
                            onClick={() => setReportProperty(property)}
                          >
                            <Download size={12} />
                          </Button>
                        </ActionButtonsSmall>
                      </CardActions>
                    </CardContent>
                  </PropertyCard>
                );
              })}
            </PropertyGrid>

            {filteredProperties.length === 0 && (
              <EmptyState
                type="properties"
                title="No properties found"
                description="Try adjusting your search or filters, or add your first property to get started"
                actions={
                  <Button variant="primary" size="md" onClick={() => setShowAddModal(true)}>
                    <Plus size={16} />
                    Add Property
                  </Button>
                }
                size="md"
              />
            )}
          </CardContent>
          </>
          )}
        </Card>

        {/* Add Property Drawer */}
        <AddPropertyDrawer
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateProperty}
          isLoading={isSubmitting}
        />

        {/* Edit Property Drawer */}
        <AddPropertyDrawer
          isOpen={!!editingProperty}
          onClose={() => setEditingProperty(null)}
          onSubmit={handleUpdateProperty}
          property={editingProperty}
          isLoading={isSubmitting}
          onLocationPatched={handleLocationPatched}
        />

        {/* Lease Editor Modal (doc icon on property card) – uses React Query for fetch + mutations */}
        <LeaseEditorModal
          isOpen={!!leaseEditorProperty}
          onClose={() => { setLeaseEditorProperty(null); setLeaseEditorLeaseId(null); }}
          propertyId={leaseEditorProperty?.id ?? ''}
          propertyName={leaseEditorProperty?.name}
          leaseId={leaseEditorLeaseId ?? undefined}
        />



        {/* View Property Modal */}
        <Modal
          isOpen={!!viewingProperty}
          onClose={() => setViewingProperty(null)}
          title={viewingProperty ? `Property Details · ${viewingProperty.name}` : 'Property Details'}
          size="md"
        >
          {viewingProperty && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(() => {
                const links = getMapLinks(viewingProperty);
                const quality = getLocationQuality(viewingProperty);
                return (
                  <>
              <ViewPropertyGrid>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Location</ViewPropertyLabel>
                  <ViewPropertyValue>{viewingProperty.location}</ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Type</ViewPropertyLabel>
                  <ViewPropertyValue>{viewingProperty.type}</ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Status</ViewPropertyLabel>
                  <ViewPropertyValue>{viewingProperty.status}</ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Total Units</ViewPropertyLabel>
                  <ViewPropertyValue>{viewingProperty.totalUnits}</ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Occupied</ViewPropertyLabel>
                  <ViewPropertyValue>
                    {viewingProperty.units?.filter((u: any) => u.status === 'OCCUPIED').length || 0}
                  </ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Vacant</ViewPropertyLabel>
                  <ViewPropertyValue>
                    {Math.max(0, (viewingProperty.units?.length || 0) - (viewingProperty.units?.filter((u: any) => u.status === 'OCCUPIED').length || 0))}
                  </ViewPropertyValue>
                </ViewPropertyItem>
                <ViewPropertyItem>
                  <ViewPropertyLabel>Location Quality</ViewPropertyLabel>
                  <ViewPropertyValue style={{ color: quality.tone }}>{quality.label}</ViewPropertyValue>
                </ViewPropertyItem>
              </ViewPropertyGrid>

              {typeof viewingProperty.latitude === 'number' && typeof viewingProperty.longitude === 'number' && (
                <ViewPropertyItem style={{ padding: 0, overflow: 'hidden' }}>
                  <ViewPropertyLabel style={{ padding: '10px 12px 0' }}>Map Preview</ViewPropertyLabel>
                  <div style={{ height: 220, borderTop: '1px solid var(--border)' }}>
                    <iframe
                      title="Property location preview"
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      loading="lazy"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${viewingProperty.longitude - 0.01}%2C${viewingProperty.latitude - 0.01}%2C${viewingProperty.longitude + 0.01}%2C${viewingProperty.latitude + 0.01}&layer=mapnik&marker=${viewingProperty.latitude}%2C${viewingProperty.longitude}`}
                    />
                  </div>
                </ViewPropertyItem>
              )}

              <ViewPropertyItem>
                <ViewPropertyLabel>Property ID</ViewPropertyLabel>
                <ViewPropertyValue>{viewingProperty.id}</ViewPropertyValue>
              </ViewPropertyItem>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', overflowX: 'auto' }}>
                <Button variant="outline" size="sm" onClick={() => window.open(links.google, '_blank', 'noopener,noreferrer')}>
                  <Navigation size={13} style={{ marginRight: 6 }} />
                  Google Maps
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isRegeocodingPropertyId === viewingProperty.id}
                  onClick={() => handleReGeocodeProperty(viewingProperty)}
                >
                  Re-geocode location
                </Button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
                <Button variant="outline" onClick={() => setViewingProperty(null)}>Close</Button>
                <Button onClick={() => {
                  setViewingProperty(null);
                  handleNavigateToUnits(viewingProperty.id, viewingProperty.status);
                }}>
                  <Layers size={14} style={{ marginRight: 6 }} />
                  Open Units
                </Button>
              </div>
                  </>
                );
              })()}
            </div>
          )}
        </Modal>

        {/* Plan modal after first property */}
        <Modal
          isOpen={showPlanModal}
          onClose={() => { setShowPlanModal(false); setPlanModalData(null); }}
          title="Your plan"
          size="sm"
        >
          <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {planModalData ? (
              <>
                {planModalData.bandLabel && (
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    Your band: {planModalData.bandLabel}
                  </div>
                )}
                <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>
                  {planModalData.isCustom
                    ? 'Custom (100+ units) — contact sales for pricing.'
                    : planModalData.amount != null
                      ? `Est. monthly SaaS cost: ${planModalData.currency ?? 'KES'} ${(planModalData.amount as number).toLocaleString()}`
                      : '—'}
                </div>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>
                  You can manage your plan in Settings → Account.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <Button variant="primary" size="md" onClick={() => { setShowPlanModal(false); setPlanModalData(null); }}>
                    Got it
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Loading…</div>
            )}
          </div>
        </Modal>

        <PropertyReportPeriodModal
          isOpen={!!reportProperty}
          onClose={() => {
            if (isDownloadingReport) return;
            setReportProperty(null);
          }}
          propertyId={reportProperty?.id ?? null}
          propertyName={reportProperty?.name}
          onDownload={({ month, year }) => handleDownloadPropertyReport(month, year)}
          isDownloading={isDownloadingReport}
        />

        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onCancel={() => {
            if (isDeletingProperty) return;
            setShowDeleteConfirm(false);
            setPropertyToDelete(null);
          }}
          onConfirm={handleConfirmDeleteProperty}
          title="Delete Property"
          description="Are you sure you want to delete this property? This action cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          isLoading={isDeletingProperty}
        />

      </ContentContainer>
    </PageContainer>
  );
};

export default Properties; 